/**
 * @fileoverview 船舶 詳細ドロワー（船舶管理）
 * @description 人材管理の TalentDetailDrawer に倣った、右からスライドするカルテ型の船舶詳細。
 *              「基本情報 / 乗組員 / 運航設定」の3タブで、諸元・証明書用情報・所有者・
 *              乗船中の乗組員・業務種別/特別作業を読み取り専用で確認できる。
 *              一覧の行クリックで vesselId 指定で開き、ヘッダーの「編集」から編集モーダルへ遷移する。
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Ship, X, Anchor, Gauge, FileText, Building2, Wrench,
  Pencil, ClipboardList, IdCard, AlertCircle, CheckCircle2,
  Navigation, Compass, Clock, MapPin,
} from 'lucide-react';
import * as api from '../services/api';
import AvatarEditor from './AvatarEditor';
import { Pill, fmtDate, daysSince } from './TalentDetailDrawer';
import VesselTrailMap, { type VesselData, type TrailPoint } from './VesselTrailMap';

// ── 型 ──────────────────────────────────────────────────────────────────────
interface VesselCrew {
  id: string;
  user_name: string;
  user_role: string;
  short_id?: string | null;
  boarded_at?: string | null;
  assignment_status?: string;
  position?: string | null;
  avatar_path?: string | null;
}
interface VesselFull {
  id: string;
  vessel_name: string;
  vessel_type?: string | null;
  capacity?: number;
  long_break_hours?: number;
  image_path?: string | null;
  created_at?: string;
  vessel_no?: string | null;
  imo_number?: string | null;
  gross_tonnage?: string | null;
  port_of_registry?: string | null;
  main_engine_type?: string | null;
  main_engine_count?: string | null;
  main_engine_power?: string | null;
  owner_type?: string | null;
  owner_name?: string | null;
  owner_address?: string | null;
}
interface WorkType { id: string; name: string; emoji: string; requires_alcohol: number }
interface ActivityType { master: { id: string; name: string; color: string }; is_active: number }

const OWNER_TYPE_LABELS: Record<string, string> = {
  self: '自社', owner: '船舶所有者', manager: '船舶管理人', charterer: '船舶借入人',
};
const ROLE_LABELS: Record<string, string> = {
  crew: '船員', captain: '船長', shore_admin: '陸上管理者', system_admin: 'システム管理者', admin: '管理者',
};
// 運航状態（GPS・打刻からの自動判定）
const GPS_STATUS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  sailing:  { label: '航行中',     color: '#0369a1', bg: '#e0f2fe', border: '#7dd3fc' },
  port:     { label: '入港中',     color: '#15803d', bg: '#dcfce7', border: '#bbf7d0' },
  anchored: { label: '停泊中',     color: '#475569', bg: '#f1f5f9', border: '#cbd5e1' },
  offline:  { label: 'オフライン', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
};
/** 日時を "M/D HH:mm" で表記（GPS最終取得時刻用）*/
function fmtDateTime(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

interface GpsVessel {
  id: string;
  vessel_name: string;
  lat: number;
  lng: number;
  status: string;
  speed: number;
  heading: number;
  trail: TrailPoint[];
}

// ── 小さめ共有UI（TalentDetailDrawer の見た目に合わせたローカル版）──────────
const InfoItem: React.FC<{ label: string; children: React.ReactNode; span2?: boolean }> = ({ label, children, span2 }) => (
  <div style={{ gridColumn: span2 ? '1 / -1' : undefined }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13.5, color: '#1e293b', fontWeight: 500, wordBreak: 'break-word' }}>{children}</div>
  </div>
);
const Missing: React.FC = () => <span style={{ color: '#cbd5e1', fontWeight: 400 }}>未登録</span>;
const val = (v?: string | number | null): React.ReactNode =>
  (v === null || v === undefined || v === '') ? <Missing /> : v;

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; right?: React.ReactNode }> = ({ icon, title, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 10px' }}>
    <span style={{ color: '#0369a1', display: 'flex' }}>{icon}</span>
    <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{title}</span>
    <div style={{ marginLeft: 'auto' }}>{right}</div>
  </div>
);

// ── 本体 ────────────────────────────────────────────────────────────────────
type DrawerTab = 'info' | 'crew' | 'gps' | 'ops';

const VesselDetailDrawer: React.FC<{
  vesselId: string;
  onClose: () => void;
  onEdit: (id: string) => void;
  onChanged: () => void;   // 画像変更などを一覧へ反映
}> = ({ vesselId, onClose, onEdit, onChanged }) => {
  const [vessel, setVessel] = useState<VesselFull | null>(null);
  const [crew, setCrew] = useState<VesselCrew[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<DrawerTab>('info');
  // GPS（航跡）は該当タブを開いたときに初めて取得する
  const [gps, setGps] = useState<GpsVessel | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsLoaded, setGpsLoaded] = useState(false);

  const load = useCallback(async () => {
    setGps(null); setGpsLoaded(false);
    try {
      const res = await api.get<any>(`/vessels/${vesselId}`);
      setVessel(res?.data?.vessel ?? null);
      setCrew(res?.data?.crew ?? []);
    } catch (e) { console.error('[Vessel/detail]', e); }
    // 運航設定は付随情報なので失敗しても本体は表示する
    api.get<any>(`/vessels/${vesselId}/work-types`).then(r => setWorkTypes(r?.data ?? [])).catch(() => {});
    api.get<any>(`/vessels/${vesselId}/activity-types`).then(r => setActivityTypes((r?.data ?? []).filter((a: ActivityType) => a.is_active === 1))).catch(() => {});
    setLoading(false);
  }, [vesselId]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  // GPSタブを開いたら航跡（直近24時間）を取得
  useEffect(() => {
    if (tab !== 'gps' || gpsLoaded) return;
    setGpsLoading(true);
    api.get<any>(`/vessels/gps/trail?hours=24`)
      .then(r => { setGps((r?.vessels ?? []).find((x: GpsVessel) => x.id === vesselId) ?? null); })
      .catch(() => setGps(null))
      .finally(() => { setGpsLoading(false); setGpsLoaded(true); });
  }, [tab, gpsLoaded, vesselId]);

  // ESC で閉じる
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const v = vessel;
  // 証明書用情報の充足度
  const certFields = v ? [v.vessel_no, v.gross_tonnage, v.port_of_registry, v.main_engine_type, v.main_engine_power] : [];
  const certFilled = certFields.filter(Boolean).length;
  const certTotal = certFields.length;
  const certComplete = certTotal > 0 && certFilled === certTotal;

  const TABS: { key: DrawerTab; icon: React.ReactNode; label: string }[] = [
    { key: 'info', icon: <IdCard size={14} />, label: '基本情報' },
    { key: 'crew', icon: <Anchor size={14} />, label: `乗組員${crew.length ? `（${crew.length}）` : ''}` },
    { key: 'gps', icon: <Navigation size={14} />, label: '位置・航跡' },
    { key: 'ops', icon: <ClipboardList size={14} />, label: '運航設定' },
  ];

  const engineText = v
    ? [v.main_engine_type, v.main_engine_count ? `${v.main_engine_count}基` : '', v.main_engine_power ? `${v.main_engine_power}kW` : ''].filter(Boolean).join('　')
    : '';

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 900 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 94vw)', zIndex: 901,
        background: '#fff', boxShadow: '-12px 0 40px rgba(15,23,42,0.18)',
        display: 'flex', flexDirection: 'column', animation: 'vesselDrawerIn .22s ease',
      }}>
        <style>{`@keyframes vesselDrawerIn { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>

        {/* ヘッダー */}
        <div style={{ padding: '18px 24px 0', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {v && (
              <AvatarEditor entityId={v.id} type="vessel" imagePath={v.image_path} displayName={v.vessel_name} size={72}
                onUpdated={() => onChanged()} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              {v ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Ship size={18} color="#0369a1" />{v.vessel_name}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    {v.vessel_type && <Pill bg="#eff6ff" fg="#2563eb" border="#bfdbfe">{v.vessel_type}</Pill>}
                    <Pill bg={crew.length ? '#f0fdf4' : '#f1f5f9'} fg={crew.length ? '#16a34a' : '#64748b'} border={crew.length ? '#bbf7d0' : '#e2e8f0'}>
                      <Anchor size={11} />乗船中 {crew.length}名
                    </Pill>
                    {v.capacity != null && <Pill bg="#f1f5f9" fg="#475569" border="#e2e8f0">定員 {v.capacity}名</Pill>}
                    {certComplete
                      ? <Pill bg="#f0fdf4" fg="#16a34a" border="#bbf7d0"><CheckCircle2 size={11} />証明書情報 完備</Pill>
                      : <Pill bg="#fffbeb" fg="#b45309" border="#fde68a"><AlertCircle size={11} />証明書情報 {certFilled}/{certTotal}</Pill>}
                  </div>
                </>
              ) : <span style={{ color: '#94a3b8' }}>読み込み中…</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}>
              {v && (
                <button onClick={() => onEdit(v.id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>
                  <Pencil size={13} />編集
                </button>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 6 }}>
                <X size={20} />
              </button>
            </div>
          </div>

          {/* タブバー */}
          <div style={{ display: 'flex', gap: 2, marginTop: 14 }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  border: 'none', background: 'transparent',
                  color: tab === t.key ? '#0369a1' : '#64748b',
                  borderBottom: tab === t.key ? '2.5px solid #0369a1' : '2.5px solid transparent',
                  marginBottom: -1,
                }}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ボディ */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 24px 28px' }}>
          {loading && <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中…</div>}
          {!loading && v && (
            <>
              {/* ═══ 基本情報 ═══ */}
              {tab === 'info' && (
                <>
                  <SectionTitle icon={<Gauge size={15} />} title="諸元" />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                    <InfoItem label="種別">{val(v.vessel_type)}</InfoItem>
                    <InfoItem label="定員">{v.capacity != null ? `${v.capacity}名` : <Missing />}</InfoItem>
                    <InfoItem label="船舶番号">{val(v.vessel_no)}</InfoItem>
                    <InfoItem label="IMO番号">{val(v.imo_number)}</InfoItem>
                  </div>

                  <SectionTitle icon={<FileText size={15} />} title="証明書用情報"
                    right={certComplete
                      ? <Pill bg="#f0fdf4" fg="#16a34a" border="#bbf7d0">完備</Pill>
                      : <Pill bg="#fffbeb" fg="#b45309" border="#fde68a">{certFilled}/{certTotal} 入力済</Pill>} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                    <InfoItem label="総トン数">{v.gross_tonnage ? `${v.gross_tonnage} トン` : <Missing />}</InfoItem>
                    <InfoItem label="船籍港">{val(v.port_of_registry)}</InfoItem>
                    <InfoItem label="主機の種類、箇数及び出力" span2>{engineText || <Missing />}</InfoItem>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    ※ 雇入届・航行報告書などの<span style={{ color: '#0369a1', fontWeight: 700 }}> 証明書発行</span> 時に自動反映されます。
                  </div>

                  <SectionTitle icon={<Building2 size={15} />} title="所有者情報" />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                    <InfoItem label="届出区分">{OWNER_TYPE_LABELS[v.owner_type ?? 'self'] ?? '自社'}</InfoItem>
                    <InfoItem label={`${OWNER_TYPE_LABELS[v.owner_type ?? 'self'] ?? '所有者'}名`}>
                      {v.owner_type && v.owner_type !== 'self' ? val(v.owner_name) : <span style={{ color: '#64748b' }}>自社</span>}
                    </InfoItem>
                    <InfoItem label="住所" span2>{v.owner_type && v.owner_type !== 'self' ? val(v.owner_address) : <Missing />}</InfoItem>
                  </div>

                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 16 }}>登録日: {fmtDate(v.created_at)}</div>
                </>
              )}

              {/* ═══ 乗組員 ═══ */}
              {tab === 'crew' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 16 }}>
                    {[
                      { label: '現在の乗船', value: `${crew.length}名`, sub: v.capacity != null ? `定員 ${v.capacity}名` : '' },
                      { label: '空き', value: v.capacity != null ? `${Math.max(0, v.capacity - crew.length)}名` : '-', sub: '定員に対する残枠' },
                    ].map(s => (
                      <div key={s.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>{s.label}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#0369a1', lineHeight: 1.3 }}>{s.value}</div>
                        <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{s.sub}</div>
                      </div>
                    ))}
                  </div>

                  <SectionTitle icon={<Anchor size={15} />} title="乗船中の乗組員" />
                  {crew.length === 0 ? (
                    <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '18px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
                      現在この船に乗船中の乗組員はいません
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {crew.map(c => {
                        const days = daysSince(c.boarded_at);
                        return (
                          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 14px', background: '#fff' }}>
                            <AvatarEditor entityId={c.id} type="user" imagePath={c.avatar_path} displayName={c.user_name} size={40} readOnly onUpdated={() => {}} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{c.user_name}</span>
                                {c.position && <Pill bg="#eff6ff" fg="#2563eb" border="#bfdbfe">{c.position}</Pill>}
                                {c.user_role === 'captain' && <Pill bg="#fef3c7" fg="#92400e" border="#fde68a">⭐ 船長</Pill>}
                              </div>
                              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                                {ROLE_LABELS[c.user_role] ?? c.user_role}
                                {c.boarded_at && <span> ・ 乗船 {fmtDate(c.boarded_at)}{days != null ? `（${days + 1}日目）` : ''}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                    乗組員の配置換えは <span style={{ color: '#0369a1', fontWeight: 700 }}>乗組員配置</span> ページで行えます。
                  </div>
                </>
              )}

              {/* ═══ 位置・航跡（GPS）═══ */}
              {tab === 'gps' && (() => {
                if (gpsLoading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>位置情報を取得中…</div>;
                const posUnknown = Boolean((gps as any)?.position_unknown);
                const hasPos = gps && !posUnknown && (gps.trail?.length > 0 || gps.lat != null);
                if (!hasPos) return (
                  <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '28px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13, marginTop: 16 }}>
                    <MapPin size={26} style={{ opacity: 0.5, marginBottom: 8 }} /><br />
                    {posUnknown ? '位置不明（船内端末のGPSが取得できていません）' : 'この船舶のGPS位置情報はまだありません'}<br />
                    <span style={{ fontSize: 11 }}>（船内端末で測位が開始されると表示されます。乗組員個人のスマホの位置は船の位置には使いません）</span>
                  </div>
                );
                const g = gps!;
                const st = GPS_STATUS[g.status] ?? GPS_STATUS.offline!;
                const trail = (g.trail ?? []) as TrailPoint[];
                const effTrail: TrailPoint[] = trail.length
                  ? trail
                  : [{ ts: Date.now(), lat: g.lat, lng: g.lng, speed: g.speed ?? 0, heading: g.heading ?? 0 }];
                const lastTs = effTrail.reduce((m, p) => Math.max(m, p.ts), 0);
                const last = effTrail[effTrail.length - 1]!;
                const vd: VesselData = { id: g.id, name: g.vessel_name, code: '', status: g.status, trail: effTrail, color: st.color };
                return (
                  <>
                    <SectionTitle icon={<Navigation size={15} />} title="現在の状況"
                      right={<Pill bg={st.bg} fg={st.color} border={st.border}>{st.label}</Pill>} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      {[
                        { icon: <Gauge size={14} />, label: '速力', value: `${(last.speed ?? 0).toFixed(1)}`, unit: 'kt' },
                        { icon: <Compass size={14} />, label: '針路', value: `${Math.round(last.heading ?? 0)}`, unit: '°' },
                        { icon: <Clock size={14} />, label: '最終測位', value: fmtDateTime(lastTs), unit: '' },
                      ].map(s => (
                        <div key={s.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>{s.icon}{s.label}</div>
                          <div style={{ fontSize: 17, fontWeight: 800, color: '#0369a1', lineHeight: 1.4 }}>{s.value}<span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginLeft: 2 }}>{s.unit}</span></div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <MapPin size={13} color="#94a3b8" />
                      {last.lat.toFixed(4)}°N, {last.lng.toFixed(4)}°E
                    </div>

                    <SectionTitle icon={<MapPin size={15} />} title="直近24時間の航跡"
                      right={<span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{trail.length}点</span>} />
                    <div style={{ height: 340, borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <VesselTrailMap vessels={[vd]} selectedId={g.id} currentTime={lastTs} showTrail showLabel={false} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                      すべての船舶の位置・航跡・リプレイは <span style={{ color: '#0369a1', fontWeight: 700 }}>GPS追跡</span> ページで確認できます。
                    </div>
                  </>
                );
              })()}

              {/* ═══ 運航設定 ═══ */}
              {tab === 'ops' && (
                <>
                  <SectionTitle icon={<ClipboardList size={15} />} title="業務種別" />
                  {workTypes.length === 0 ? (
                    <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
                      設定された業務種別はありません
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {workTypes.map(w => (
                        <span key={w.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                          <span style={{ fontSize: 16 }}>{w.emoji}</span>{w.name}
                          {w.requires_alcohol === 1 && (
                            <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 3, padding: '1px 5px' }}>アルコール</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}

                  <SectionTitle icon={<Wrench size={15} />} title="特別作業" />
                  {activityTypes.length === 0 ? (
                    <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
                      設定された特別作業はありません
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {activityTypes.map(a => (
                        <span key={a.master.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: a.master.color ?? '#6366f1' }} />
                          {a.master.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
                    業務種別・特別作業の変更は「編集」から行えます。
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VesselDetailDrawer;
